import { requisicaoAutenticada } from './http';

/**
 * Leitura da operação de ICMS: clientes, fornecedores e assessores.
 *
 * ## Por que um serviço só para os três
 *
 * Do ponto de vista do banco eles são a mesma coisa — uma `Business` com uma
 * especialização 1:1 — e as três listagens devolvem exatamente o mesmo formato.
 * Separar em três arquivos duplicaria o tipo e a tradução de erro três vezes
 * para ganhar nomes de arquivo.
 *
 * O que **não** vem para cá é o cadastro da empresa (`/business`): lá se altera
 * o registro, aqui se lê a carteira. São telas diferentes e permissões
 * diferentes.
 */

/** O núcleo comum às três listagens. */
export interface ParteDaOperacao {
  readonly businessId: string;
  readonly razaoSocial: string;
  readonly nomeFantasia: string | null;
  readonly cnpj: string;
  /**
   * Estado da operação — só a listagem de **clientes** preenche.
   *
   * Vem do contrato mais recente, não do cadastro: um cliente pode ter
   * fornecedores diferentes em meses diferentes (RN-149). `null` em
   * `statusContrato` significa "ainda sem contrato", que é diferente de ABERTO.
   */
  readonly statusContrato?: string | null;
  readonly anoContrato?: number | null;
  readonly fornecedor?: string | null;
  readonly assessor?: string | null;
  /**
   * Todos os contratos ativos do cliente (só a listagem de clientes manda).
   * O sino conta contratos; filtrada pela etapa, a carteira abre uma linha por
   * contrato com estes dados, para o número bater com o aviso.
   */
  readonly contratosAtivos?: readonly {
    readonly ano: number;
    readonly mes: number;
    readonly status: string;
    readonly fornecedor: string | null;
  }[];
  /** Só nas linhas abertas por contrato: a mesma empresa pode vir mais de uma vez. */
  readonly chaveDaLinha?: string;
}

export type PapelDaCarteira = 'clientes' | 'fornecedores' | 'assessores';

/** O caminho de cada papel na API, que ainda usa os nomes em inglês. */
const CAMINHO: Readonly<Record<PapelDaCarteira, string>> = {
  clientes: '/icms/clients',
  fornecedores: '/icms/suppliers',
  assessores: '/icms/advisors',
};

/**
 * A carteira de um papel. Com `empresa`, só o que é dela (quem não é admin
 * passa a empresa escolhida: a API recusa empresa que não é da pessoa).
 *
 * `todos` (só clientes): também quem não tem operação de Recorrente — a lista
 * de escolha do comprador no Pontual e do cliente no Passivos.
 */
export async function listarCarteira(
  papel: PapelDaCarteira,
  empresa?: string,
  opcoes: { readonly todos?: boolean } = {},
): Promise<ParteDaOperacao[]> {
  const query = new URLSearchParams();
  if (empresa !== undefined) query.set('businessId', empresa);
  if (opcoes.todos === true) query.set('todos', '1');
  const filtro = query.toString() === '' ? '' : `?${query.toString()}`;
  return requisicaoAutenticada<ParteDaOperacao[]>(`${CAMINHO[papel]}${filtro}`);
}

export interface VinculoDaOperacao {
  readonly parceiroId: string;
  readonly fornecedorId: string;
}

export interface ContratoResumo {
  readonly ano: number;
  readonly mes: number;
  readonly fornecedorId: string;
  readonly parceiroId: string | null;
  readonly status: string;
}

export interface ClienteDetalhado extends ParteDaOperacao {
  /** RN-621 — o assessor responsável pelo cliente. */
  readonly assessorResponsavel: {
    readonly businessId: string;
    readonly razaoSocial: string;
  } | null;
  readonly vinculos: readonly VinculoDaOperacao[];
  readonly contratos: readonly ContratoResumo[];
}

export async function obterCliente(clienteId: string): Promise<ClienteDetalhado> {
  return requisicaoAutenticada<ClienteDetalhado>(`/icms/clients/${clienteId}`);
}

/**
 * Linha de cálculo mensal.
 *
 * `percentSpedPercentual` e `percentBeneficioPercentual` carregam a escala no
 * nome, e não é preciosismo: o mesmo campo era gravado em fração e lido como
 * percentual por dois geradores de e-mail no legado, e o contribuinte recebeu um
 * número 100× maior que o real (RN-078). O nome longo é o que impede a tela de
 * adivinhar.
 */
export interface LinhaSped {
  readonly id: string;
  readonly competencia: string;
  readonly industrial: boolean;
  readonly totalSped: number;
  readonly percentSpedPercentual: number;
  readonly valueSped: number;
  readonly percentBeneficioPercentual: number | null;
  readonly valueBeneficio: number | null;
  readonly total: number | null;
  readonly fornecedorId: string | null;
  /**
   * RN-151 — quem atende o mês: o fornecedor materializado no SPED ou, enquanto
   * não há materialização, o do contrato atual do ano. Vem resolvido do
   * servidor porque a lista de fornecedores que a tela enxerga é escopada ao
   * requisitante — um usuário do cliente não necessariamente recebe o
   * fornecedor dele nela, e não conseguiria traduzir o id em nome.
   */
  readonly fornecedorNome: string | null;
  readonly statusEnvio: string;
}

export async function listarSpeds(clienteId: string): Promise<LinhaSped[]> {
  return requisicaoAutenticada<LinhaSped[]>(`/icms/speds/${clienteId}`);
}

export type PapelNoFluxo = 'CLIENTE' | 'FORNECEDOR' | 'ASSESSOR' | 'MS3';

export interface DocumentoDaLinha {
  readonly chave: string;
  readonly rotulo: string;
  readonly de: PapelNoFluxo;
  readonly deRotulo: string;
  readonly papel: 'RESPOSTA' | 'LEVA';
  readonly recebido: boolean;
}

export interface ProximaAcaoDaLinha {
  readonly responsavel: PapelNoFluxo | null;
  readonly responsavelRotulo: string | null;
  /** Curto, cabe na linha da tabela. */
  readonly resumo: string;
  /** A frase inteira, para o detalhe do item. */
  readonly detalhe: string;
  readonly tom: 'aguardando' | 'atencao' | 'concluido' | 'neutro';
}

export interface LinhaCronograma {
  readonly id: string;
  readonly emailId: string;
  /** O assunto do e-mail no Postmark, com mês e razão social preenchidos. */
  readonly titulo: string;
  readonly status: string;
  readonly dataAgendamento: string;
  readonly dataEnvio: string | null;
  readonly competencia: string | null;
  /** Mês de operação (`AAAA-MM`, SPED + 1 ano); `null` = não é de um mês. */
  readonly mesOperacao: string | null;
  /** Arquivos anexados ao item. */
  readonly anexos: number;
  /** O passo do fluxo do mês; `null` para envio avulso. */
  readonly passo: {
    readonly ordem: number;
    readonly rotuloCurto: string;
    readonly paraQuem: PapelNoFluxo;
    readonly paraQuemRotulo: string;
    readonly oQueAcontece: string;
  } | null;
  /** O que a etapa espera de volta (`RESPOSTA`) e o que o e-mail leva (`LEVA`). */
  readonly documentos: readonly DocumentoDaLinha[];
  /** De quem é a bola, e o que precisa acontecer para o mês andar. */
  readonly proximaAcao: ProximaAcaoDaLinha;
  /** Tipo da fila: pode ser enviado/reenviado por "Enviar agora". */
  readonly reenviavel: boolean;
  readonly fornecedorId: string | null;
  readonly parceiroId: string | null;
}

export async function listarCronograma(clienteId: string): Promise<LinhaCronograma[]> {
  return requisicaoAutenticada<LinhaCronograma[]>(`/icms/cronograma/${clienteId}`);
}

/** RN-082 — um mês de SPED e os tipos de e-mail que ele ainda não tem na fila. */
export interface SpedDisponivel {
  readonly calculoId: string;
  readonly competencia: string;
  readonly totalSped: number;
  readonly valueSped: number;
  readonly tiposFaltantes: readonly string[];
}

export async function listarSpedsDisponiveis(
  clienteId: string,
  ano: number,
): Promise<SpedDisponivel[]> {
  return requisicaoAutenticada<SpedDisponivel[]>(
    `/icms/cronograma/${clienteId}/disponiveis/${ano}`,
  );
}

export interface MesDoDashboard {
  readonly competencia: string;
  readonly valorUtilizacao: number;
  readonly beneficioEconomico: number;
  readonly valorPago: number;
  readonly desagioVendaPercentual: number;
  readonly fornecedorId: string | null;
  readonly fornecedorNome: string | null;
  readonly statusContrato: string | null;
  readonly origemDesagio: string;
}

export interface DashboardCliente {
  readonly clienteId: string;
  readonly ano: number;
  /**
   * Ano dos arquivos que alimentam o aproveitamento.
   *
   * É sempre `ano - 1`: o crédito aproveitado num exercício vem da apuração do
   * anterior (RN-619). O campo existe para a tela poder dizer isso em vez de
   * deixar o operador achar que os meses estão errados.
   */
  readonly anoSped: number;
  readonly meses: readonly MesDoDashboard[];
}

export async function obterDashboardCliente(
  clienteId: string,
  ano: number,
): Promise<DashboardCliente> {
  return requisicaoAutenticada<DashboardCliente>(`/icms/dashboard/clientes/${clienteId}/${ano}`);
}
